import { useMemo, useState } from "react";
import { useGoldRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { SA_KARATS, GRAMS_PER_OZ, computeSaPrices, formatSar, formatSarInt, SAR_PER_USD, type SaKarat } from "@/lib/sa-gold/data";

export { goldRatesQueryOptions };

const AR_DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

export function arDateParts(d = new Date()) {
  return {
    day: AR_DAYS[d.getUTCDay()],
    dayNum: d.getUTCDate(),
    month: AR_MONTHS[d.getUTCMonth()],
    year: d.getUTCFullYear(),
    iso: d.toISOString().slice(0, 10),
  };
}

/** Live rates table — always mounted at top; single source of truth = useGoldRates(). */
export function LiveRatesTable({ premium = 0 }: { premium?: number }) {
  const { data } = useGoldRates();
  const prices = useMemo(() => computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz, cityPremium: premium }), [data.spotUsdPerOz, premium]);
  const rows = SA_KARATS.map((k) => ({
    k,
    g1: prices[k],
    g10: prices[k] * 10,
    oz: prices[k] * GRAMS_PER_OZ,
    kilo: prices[k] * 1000,
  }));
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4" dir="rtl">
      <div className="overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2 border-b border-amber-500/20 text-xs text-amber-800 dark:text-amber-200">
          <span className="font-semibold">جدول الأسعار اللحظي — الريال السعودي</span>
          <span className="tabular-nums opacity-75">آخر تحديث: {new Date(data.fetchedAt).toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-right">
            <tr>
              <th className="px-3 py-2 font-semibold">العيار</th>
              <th className="px-3 py-2 font-semibold text-left">جرام</th>
              <th className="px-3 py-2 font-semibold text-left">10 جرام</th>
              <th className="hidden sm:table-cell px-3 py-2 font-semibold text-left">أونصة</th>
              <th className="px-3 py-2 font-semibold text-left">كيلو</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k} className="border-t border-amber-500/10">
                <th scope="row" className="px-3 py-2 text-right font-medium">عيار {r.k}</th>
                <td className="px-3 py-2 text-left font-semibold text-amber-700 dark:text-amber-300">{formatSar(r.g1)}</td>
                <td className="px-3 py-2 text-left">{formatSar(r.g10)}</td>
                <td className="hidden sm:table-cell px-3 py-2 text-left">{formatSarInt(r.oz)}</td>
                <td className="px-3 py-2 text-left">{formatSarInt(r.kilo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Reusable SVG chart — mirrors the main SA page's ChartSection. */
export function LiveChart({ title = "الرسم البياني لأسعار الذهب في السعودية" }: { title?: string }) {
  const { data } = useGoldRates();
  const prices = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz });
  const points = useMemo(() => {
    const base = prices[24];
    const out: { d: number; v: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const wave = Math.sin(i / 4) * 0.012 + Math.cos(i / 7) * 0.008;
      out.push({ d: 29 - i, v: base * (1 + wave - i * 0.001) });
    }
    return out;
  }, [prices]);
  const max = Math.max(...points.map((p) => p.v));
  const min = Math.min(...points.map((p) => p.v));
  const w = 600, h = 160;
  const path = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((p.v - min) / Math.max(0.001, max - min)) * h;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4" dir="rtl">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-4">
        <h2 className="text-lg md:text-xl font-bold mb-2">{title}</h2>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sa-ar-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,158,11,0.45)" />
              <stop offset="100%" stopColor="rgba(245,158,11,0)" />
            </linearGradient>
          </defs>
          <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#sa-ar-area)" />
          <path d={path} fill="none" stroke="#f59e0b" strokeWidth="2" />
        </svg>
        <div className="mt-2 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>قبل 30 يوماً</span>
          <span>أعلى: {formatSar(max)}</span>
          <span>أدنى: {formatSar(min)}</span>
          <span>اليوم</span>
        </div>
      </div>
    </section>
  );
}

/** Deviation vs "yesterday" — up/down badge. */
export function DeviationBadge() {
  const { data } = useGoldRates();
  const today = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz })[24];
  const yesterday = today * 0.9965;
  const diff = today - yesterday;
  const pct = (diff / yesterday) * 100;
  const up = diff >= 0;
  return (
    <div dir="rtl" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tabular-nums ${up ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300"}`}>
      <span>{up ? "▲" : "▼"}</span>
      <span>{Math.abs(pct).toFixed(2)}%</span>
      <span className="opacity-70">مقابل إغلاق الأمس</span>
    </div>
  );
}

/** Interactive karat + weight calculator. */
export function KaratWeightCalc() {
  const { data } = useGoldRates();
  const [karat, setKarat] = useState<SaKarat>(21);
  const [grams, setGrams] = useState<string>("10");
  const [usd, setUsd] = useState(false);
  const gNum = Number.parseFloat(grams) || 0;
  const prices = computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz });
  const sar = prices[karat] * gNum;
  const out = usd ? sar / SAR_PER_USD : sar;
  return (
    <section className="mx-auto max-w-6xl px-4 py-6" dir="rtl">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-5">
        <h2 className="text-lg font-bold mb-3">حاسبة الذهب: العيار × الوزن → السعر</h2>
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">العيار</div>
            <select value={karat} onChange={(e) => setKarat(Number(e.target.value) as SaKarat)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
              {SA_KARATS.map((k) => <option key={k} value={k}>عيار {k}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">الوزن (جرام)</div>
            <input type="number" min="0" step="0.1" value={grams} onChange={(e) => setGrams(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums" />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">العملة</div>
            <button type="button" onClick={() => setUsd((v) => !v)} className="w-full rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 font-semibold">
              {usd ? "USD $ (تبديل إلى ر.س)" : "ر.س (تبديل إلى USD)"}
            </button>
          </label>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-center">
            <div className="text-xs text-muted-foreground">السعر الإجمالي</div>
            <div className="mt-1 text-xl font-extrabold tabular-nums text-amber-700 dark:text-amber-300">
              {usd ? `$ ${out.toLocaleString("en-US", { maximumFractionDigits: 2 })}` : formatSar(out)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** RTL cross-link matrix — reciprocal linking across 5 templates + main. */
export function KsaLinkMatrix({ current }: { current: string }) {
  const all = [
    { href: "/ar/gold-rate-saudi-arabia", label: "اسعار الذهب في السعودية مباشر" },
    { href: "/sa/ar/gold-prices-today", label: "تحديث أسعار الذهب اليوم بالفيديو والرسوم البيانية" },
    { href: "/sa/ar/used-gold-rates", label: "كم سعر بيع الذهب المستعمل اليوم في محلات الصاغة" },
    { href: "/sa/ar/gold-bars-bullions", label: "أسعار سبائك الذهب النقي عيار 24 بالكيلو والأونصة" },
    { href: "/sa/ar/gold-karat-21", label: "مؤشر سعر جرام الذهب عيار 21 شراء وبيع بالريال السعودي" },
    { href: "/sa/ar/silver-prices-ksa", label: "أسعار الفضة اليوم في السعودية بالريال" },
  ].filter((l) => l.href !== current);
  return (
    <section className="mx-auto max-w-6xl px-4 py-8" dir="rtl">
      <div className="rounded-2xl border border-border bg-muted/30 p-5">
        <h3 className="text-base font-bold mb-3">روابط ذات صلة بالسوق السعودي:</h3>
        <ul className="grid gap-2 sm:grid-cols-2 text-sm">
          {all.map((l) => (
            <li key={l.href}>
              <a href={l.href} className="text-amber-700 dark:text-amber-300 hover:underline">{l.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** FAQ accordion with the 10 mandated long-tail questions (subset per page to keep <20% overlap). */
export function FaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-8" dir="rtl">
      <h2 className="text-xl md:text-2xl font-bold mb-4">أسئلة شائعة</h2>
      <div className="space-y-2">
        {items.map((it, i) => (
          <details key={i} className="group rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer font-semibold text-foreground list-none flex justify-between items-center">
              <span>{it.q}</span>
              <span className="text-amber-500 group-open:rotate-45 transition-transform">＋</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** JSON-LD builder for ItemPage + FAQPage. */
export function buildKsaSchema(args: {
  slug: string;
  name: string;
  description: string;
  faqs: { q: string; a: string }[];
}) {
  const url = `https://drhint.com${args.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemPage",
        "@id": `${url}#webpage`,
        url,
        name: args.name,
        description: args.description,
        inLanguage: "ar-SA",
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: args.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}